import { split, combine } from "shamir-secret-sharing";
import { encrypt as eciesEncrypt, decrypt as eciesDecrypt } from "eciesjs";
import { keccak256 } from "ethers";

export type Holder = "buyer" | "seller" | "platform";

/**
 * The on-chain commitment array is indexed by this order. Changing it would
 * silently invalidate every commitment already written to the chain.
 */
export const HOLDERS: readonly Holder[] = ["buyer", "seller", "platform"] as const;

const THRESHOLD = 2;

/** keccak256 of a raw share. Reveals nothing — a share is uniform random bytes. */
export function commitmentOf(share: Buffer): string {
  return keccak256(share);
}

/**
 * Splits the deal key 2-of-3 and encrypts each share to its holder's secp256k1
 * public key. The wrapped shares are delivered OFF-CHAIN, once (§7.2). Only the
 * commitments go on-chain — putting a wrapped share in calldata would make it
 * permanently recoverable from block history and defeat shredding entirely (§7.1).
 *
 * ZEROISATION AND ITS LIMITS.
 *
 * Every raw share is overwritten with zeroes as soon as it has been wrapped and
 * committed to, and the caller's `dek` buffer is overwritten before this
 * function returns. Callers must therefore read whatever they need from the DEK
 * *before* calling, not after: on return the argument is 32 zero bytes.
 *
 * This is best-effort and nothing more. Node's garbage collector may have
 * copied any of these buffers elsewhere on the heap before we overwrote the
 * original — a compacting collector, a Buffer.concat, a string conversion, or
 * the crypto library's own internals can all leave a duplicate we never see and
 * cannot reach. The bytes may also have reached swap or a core dump. Overwriting
 * shortens the window; it does not close it.
 *
 * The real protection is procedural, not mechanical:
 *
 *   1. The DEK must exist only for the duration of the split. It is generated,
 *      used to encrypt the deal's fields, split here, and gone — never
 *      persisted, never logged, never held across a request boundary.
 *   2. Origination should move to the counterparty's own client, so the DEK is
 *      created and split on their machine and the platform never holds it at
 *      all. While origination runs here, the honest statement is that the
 *      platform *did* hold the whole key for a moment, and the shred guarantee
 *      rests on that moment having left no copy.
 */
export async function splitAndWrap(
  dek: Buffer,
  pubkeys: Record<Holder, string>,
): Promise<{ wrapped: Record<Holder, Buffer>; commitments: [string, string, string] }> {
  if (dek.length !== 32) throw new Error(`dek must be 32 bytes, got ${dek.length}`);

  // A copy is unavoidable — `split` wants a Uint8Array — so track it and wipe it.
  const secret = new Uint8Array(dek);
  const shares = await split(secret, HOLDERS.length, THRESHOLD);
  secret.fill(0);

  const wrapped = {} as Record<Holder, Buffer>;
  const commitments: string[] = [];

  try {
    HOLDERS.forEach((holder, i) => {
      const share = Buffer.from(shares[i]);
      wrapped[holder] = Buffer.from(eciesEncrypt(pubkeys[holder], share));
      commitments.push(commitmentOf(share));
      // Both the working copy and the buffer the library handed back.
      share.fill(0);
      shares[i].fill(0);
    });
  } finally {
    // Even on a bad public key, no share and no key survives this call.
    for (const s of shares) s.fill(0);
    dek.fill(0);
  }

  return { wrapped, commitments: commitments as [string, string, string] };
}

/** Throws if the private key does not match the key the share was wrapped to. */
export function unwrapShare(privkeyHex: string, wrapped: Buffer): Buffer {
  return Buffer.from(eciesDecrypt(privkeyHex, wrapped));
}

/** Any two shares reconstruct the deal key. One does not. */
export async function combineShares(shares: Buffer[]): Promise<Buffer> {
  return Buffer.from(await combine(shares.map((s) => new Uint8Array(s))));
}
